import React, { createContext, useContext, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useSecretManager } from "Hooks/useSecretManager";
import { OrderToCustomerPortal } from "Models/customer.portal";
import { GeneralComments } from "Models/general.comments";
import { ContainerPosition, ContainerStatus } from "Models/marine.traffic";
import { Shipment } from "Models/shipments";
import { useHistory } from "react-router-dom";
import CustomerPortalService from "Services/customer.portal.service";
import GeneralCommentsService from "Services/quote.comments.service";
import { CustomerPortalToken } from "Util/checkTokenCustomerInvite";
import env from "Util/env";
import { setWindowLocationWithTokenCustomerPortal } from "Util/setWindowLocationWithTokenCustomerPortal";

import { RequestEscalation } from "../types";
import { useAppState } from "./StateContext";

interface CustomerPortalContextProps {
  ordersAssociated: OrderToCustomerPortal[];
  isLoadingOrdersAssociated: boolean;
  onRequestEscalation: (
    requestEscalation: RequestEscalation,
    onSuccess?: () => void,
    onError?: () => void
  ) => void;
  onAddComment: (comment: GeneralComments) => void;
  onRemoveComment: (comments: GeneralComments[], commentRemoved: GeneralComments) => void;
  isLoadingComments?: boolean;
  isRequestingEscalation?: boolean;
  onChangeOrderSelected: (orderId: number) => void;
  isMileStonesOpenedInMobile: boolean;
  setIsMileStonesOpenedInMobile: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenMileStonesInMobile: () => void;
  handleCloseMileStonesInMobile: () => void;
  activeShipment?: Shipment;
  setActiveShipment: React.Dispatch<React.SetStateAction<Shipment | undefined>>;
  containerStatus?: ContainerStatus;
  setContainerStatus: React.Dispatch<React.SetStateAction<ContainerStatus | undefined>>;
  containerPosition?: ContainerPosition;
  setContainerPosition: React.Dispatch<React.SetStateAction<ContainerPosition | undefined>>;
  initialShipmentId?: number;
  setInitialShipmentId: React.Dispatch<React.SetStateAction<number | undefined>>;
  anchorEl: HTMLElement | null;
  setAnchorEl: (value: HTMLElement | null) => void;
}

const CustomerPortalContext = createContext<CustomerPortalContextProps>(
  {} as CustomerPortalContextProps
);

interface CustomerPortalProviderProps {
  children: React.ReactChild;
}

const CustomerPortalProvider = ({ children }: CustomerPortalProviderProps) => {
  const { secret } = useSecretManager(env.SECRET_ID);

  const { currentCustomerUser } = useAppState();

  const [order, setOrder] = useState<OrderToCustomerPortal>();
  const [allComments, setAllComments] = useState<GeneralComments[]>();
  const [isMileStonesOpenedInMobile, setIsMileStonesOpenedInMobile] = useState(false);
  const [activeShipment, setActiveShipment] = useState<Shipment>();
  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();
  const [containerPosition, setContainerPosition] = useState<ContainerPosition>();
  const [initialShipmentId, setInitialShipmentId] = useState<number>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const history = useHistory();

  function handleOpenMileStonesInMobile() {
    setIsMileStonesOpenedInMobile(true);
  }

  function handleCloseMileStonesInMobile() {
    setIsMileStonesOpenedInMobile(false);
  }

  const { data: ordersAssociated = [], isFetching: isLoadingOrdersAssociated } = useQuery(
    ["associated.orders.customer.portal"],
    async () => {
      const response = await CustomerPortalService.getOrders();

      if (response && response.data) {
        return response.data;
      }

      return [];
    }
  );

  const { mutate: createComment, isLoading: isCreatingComment } = useMutation(
    ["general.comments.add"],
    ({ generalComments }: { generalComments: GeneralComments }) =>
      GeneralCommentsService.createCustomerPortal(generalComments)
  );

  const { mutate: deleteComment, isLoading: isDeletingComment } = useMutation(
    ["general.comments.remove"],
    ({ id }: { id: number }) => GeneralCommentsService.deleteCustomerPortal(id)
  );

  const { mutate: sendRequestEscalation, isLoading: isRequestingEscalation } = useMutation(
    ["general.comments.remove"],
    ({ requestEscalation }: { requestEscalation: RequestEscalation }) =>
      CustomerPortalService.requestEscalation(requestEscalation)
  );

  const onAddComment = (comment: GeneralComments) => {
    createComment(
      {
        generalComments: {
          message: comment?.message,
          jobId: order?.id,
        },
      },
      {
        onSuccess: (response) => {
          if (response?.data) {
            const commentCreated = response?.data;

            if (commentCreated) {
              setAllComments([commentCreated, ...(allComments || [])]);
            }
          }
        },
      }
    );
  };

  const onRemoveComment = (
    generalCommentsUpdated: GeneralComments[],
    commentRemoved: GeneralComments
  ) => {
    if (commentRemoved && commentRemoved?.id)
      deleteComment(
        { id: commentRemoved?.id },
        {
          onSuccess: () => {
            setAllComments(generalCommentsUpdated);
          },
        }
      );
  };

  const onRequestEscalation = (
    requestEscalation: RequestEscalation,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    if (requestEscalation)
      sendRequestEscalation(
        { requestEscalation },
        {
          onSuccess: (response) => {
            if (response?.data)
              setOrder((prev) => {
                const isRequested = !prev?.requestEscalation?.isRequested;

                return {
                  ...(prev || {}),
                  requestEscalation: { ...(prev?.requestEscalation || {}), isRequested },
                } as OrderToCustomerPortal;
              });
            if (onSuccess) onSuccess();
          },
          onError,
        }
      );
  };

  const onChangeOrderSelected = (orderId: number) => {
    setWindowLocationWithTokenCustomerPortal(
      history,
      {
        jobId: orderId,
        companyId: currentCustomerUser?.companyId,
        customerId: currentCustomerUser?.customerId,
      } as CustomerPortalToken,
      secret
    );
  };

  return (
    <CustomerPortalContext.Provider
      value={{
        ordersAssociated,
        isLoadingOrdersAssociated,
        onRemoveComment,
        onAddComment,
        isLoadingComments: isCreatingComment || isDeletingComment,
        onRequestEscalation,
        isRequestingEscalation,
        onChangeOrderSelected,
        isMileStonesOpenedInMobile,
        setIsMileStonesOpenedInMobile,
        handleOpenMileStonesInMobile,
        handleCloseMileStonesInMobile,
        activeShipment,
        setActiveShipment,
        containerStatus,
        setContainerStatus,
        containerPosition,
        setContainerPosition,
        initialShipmentId,
        setInitialShipmentId,
        anchorEl,
        setAnchorEl,
      }}
    >
      {children}
    </CustomerPortalContext.Provider>
  );
};

const useCustomerPortalContext = () => {
  const context = useContext(CustomerPortalContext);

  return context;
};

export { CustomerPortalContext, CustomerPortalProvider, useCustomerPortalContext };
